import { forwardRef } from 'react';
import { getCurrentEnterpriseAppDateTimeNow } from '@mews-commander/legacy-entities/src/selectors/session/dateTime';
import { CalendarEvent } from '@mews-commander/types';
import { useLocalizeDate, useLocalizeDayName } from '@mews-commander/ui';
import {
    isDateTimeCurrentMonth,
    isDateTimeToday,
    isDateTimeWeekendDay,
    useCommanderSelector,
} from '@mews-commander/utils';
import { TimeUnit } from '@mews/enums';
import { DateTime, DateTimeService, useAppClockService } from '@mews/framework';
import { MatrixHeaderCellElement } from '../Matrix';
import { TimeUnitMatrixWrapper } from './styles';
import TimeUnitInfo from './TimeUnitInfo';

interface TimeUnitMatrixCell {
    date: DateTime;
    calendarEvents?: Array<CalendarEvent>;
    timeUnit?: TimeUnit;
}

const getCurrentTimeUnitHandler = ({
    timeUnit,
    dateTime,
    now,
}: {
    timeUnit: TimeUnit;
    dateTime: DateTime;
    now: DateTime;
}) => {
    switch (timeUnit) {
        case TimeUnit.Month:
            return isDateTimeCurrentMonth({ dateTime, now });
        case TimeUnit.Day:
            return isDateTimeToday({ dateTime, now });
        default:
            return false;
    }
};

const TimeUnitMatrixCell = forwardRef<HTMLDivElement, TimeUnitMatrixCell>(
    ({ calendarEvents, date, timeUnit = TimeUnit.Day, ...headerCellProps }, ref) => {
        const appClockService = useAppClockService();
        const now = useCommanderSelector((state) => getCurrentEnterpriseAppDateTimeNow(state, { appClockService }));

        const localizeDayName = useLocalizeDayName();
        const localizeDate = useLocalizeDate();

        const localizedDayName = localizeDayName(date);
        const localizedDate = localizeDate(DateTimeService.toDate(date));

        const isDayTimeUnit = timeUnit === TimeUnit.Day;

        return (
            <TimeUnitMatrixWrapper
                data-test-id={isDayTimeUnit && `${localizedDayName}-${localizedDate}`}
                isWeekendDay={isDayTimeUnit && isDateTimeWeekendDay(date)}
                isCurrentTimeUnit={getCurrentTimeUnitHandler({ timeUnit, dateTime: date, now })}
            >
                <MatrixHeaderCellElement {...headerCellProps} ref={ref} vertical>
                    <TimeUnitInfo calendarEvents={calendarEvents} date={date} timeUnit={timeUnit} />
                </MatrixHeaderCellElement>
            </TimeUnitMatrixWrapper>
        );
    }
);

export default TimeUnitMatrixCell;
